import React from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { css, keyframes } from '@emotion/css';
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  Chip,
  LinearProgress,
  MenuItem,
  TablePagination,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { OrderStatusPalette } from '@hopdrive/sdk-theme';
import { isMobilityRunLive, resolvePartsRunBucket } from '@hopdrive/feature-order-index';
import type { PartsRunBucket } from '@hopdrive/feature-order-index';
import { MdDirectionsCar, MdPerson, MdSearch } from 'react-icons/md';

import DefaultFallback from '@components/Fallbacks/DefaultFallback';
import Spacer from '@components/Utils/Spacer';
import type { CountWindow } from '@features/products/counts/productCounts';
import { useShellTabActions } from '@features/products/shell/shellTabActions';
import { usePartsDeliveryCounts } from '../counts/usePartsDeliveryCounts';

import ActiveRunCard from '../components/ActiveRunCard';
import CourierRunCard from '../components/CourierRunCard';
import { useCustomerConfig, useRooftop, useUser } from '@store';
import { useGetPartsDeliveryRunsQuery } from '../queries/getPartsDeliveryRuns.query.generated';
import RunStatusChip from '@components/Badges/RunStatusChip';

// ── Fulfillment source (own drivers vs 3rd-party couriers) ────────── //

type SourceKey = 'all' | 'own' | 'third_party';

const isCourierRun = (run: any): boolean => run?.fulfillment_type === 'external_courier';

const SOURCES: { key: SourceKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'own', label: '🚚 My Drivers' },
  { key: 'third_party', label: '⚡ HopDrive Managed' },
];

// ── Status config ──────────────────────────────────────────────── //

type MetricKey = 'all' | 'active' | 'scheduled' | 'completed' | 'cancelled';

interface MetricCard {
  key: MetricKey;
  label: string;
  /** The bucket the tile lists and is coloured by; the All tile has none. */
  bucket?: PartsRunBucket & keyof OrderStatusPalette;
}

const METRICS: MetricCard[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active', bucket: 'active' },
  { key: 'scheduled', label: 'Scheduled', bucket: 'upcoming' },
  { key: 'completed', label: 'Completed', bucket: 'completed' },
  { key: 'cancelled', label: 'Canceled', bucket: 'cancelled' },
];

// ── Date range presets ──────────────────────────────────────────── //

type DateRange = 'today' | 'yesterday' | 'this_week' | 'last_week' | 'this_month' | 'last_month' | 'all';

const DATE_RANGES: { value: DateRange; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'all', label: 'All Time' },
];

const getDateBounds = (range: DateRange): { start: string | null; end: string | null } => {
  const now = dayjs();
  switch (range) {
    case 'today':
      return { start: now.startOf('day').toISOString(), end: now.endOf('day').toISOString() };
    case 'yesterday':
      return { start: now.subtract(1, 'day').startOf('day').toISOString(), end: now.subtract(1, 'day').endOf('day').toISOString() };
    case 'this_week':
      return { start: now.startOf('week').toISOString(), end: now.endOf('week').toISOString() };
    case 'last_week':
      return { start: now.subtract(1, 'week').startOf('week').toISOString(), end: now.subtract(1, 'week').endOf('week').toISOString() };
    case 'this_month':
      return { start: now.startOf('month').toISOString(), end: now.endOf('month').toISOString() };
    case 'last_month':
      return { start: now.subtract(1, 'month').startOf('month').toISOString(), end: now.subtract(1, 'month').endOf('month').toISOString() };
    case 'all':
    default:
      return { start: null, end: null };
  }
};

/** All time is no window. */
const toCountWindow = (range: DateRange): CountWindow => {
  const { start, end } = getDateBounds(range);
  return start && end ? { startIso: start, endIso: end } : null;
};

// ── Helpers ──────────────────────────────────────────────── //

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const formatDeparture = (iso?: string | null) => {
  if (!iso) return null;
  return dayjs(iso).format('M/D h:mm A');
};

// ── Component ──────────────────────────────────────────────── //

/** The Active Runs tab of the Parts Delivery shell: runs in flight, by status. The layout owns the
 *  tab strip and the header; this tab contributes its refresh as the header's sync. */
const ActiveRunsTab: React.FC = () => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const user = useUser();
  const rooftop = useRooftop();
  const customerConfig = useCustomerConfig();
  const customerId = rooftop?.id || user?.profile?.customer_id || 0;
  const thirdPartyEnabled = customerConfig?.parts_delivery?.third_party === true;

  // Filter state
  const [metric, setMetric] = React.useState<MetricKey>('all');
  const [dateRange, setDateRange] = React.useState<DateRange>('all');
  const [search, setSearch] = React.useState('');
  const [source, setSource] = React.useState<SourceKey>('all');

  // Pagination
  const [offset, setOffset] = React.useState(0);
  const [limit, setLimit] = React.useState(50);

  const { data, loading, error, refetch } = useGetPartsDeliveryRunsQuery({
    variables: { customerId },
    skip: !customerId,
    fetchPolicy: 'cache-and-network',
  });

  const allRuns: any[] = data?.mobility_runs || [];

  // Filter runs based on current filters
  const filteredRuns = React.useMemo(() => {
    let filtered = allRuns;

    // Apply metric filter
    // A courier run buckets by its vendor state, an own-driver run by its run status, as the counts do.
    const bucket = METRICS.find(m => m.key === metric)?.bucket;
    if (bucket) {
      filtered = filtered.filter(run => resolvePartsRunBucket(run) === bucket);
    }

    // Apply date filter
    const { start, end } = getDateBounds(dateRange);
    if (start && end) {
      filtered = filtered.filter(run => {
        if (!run.departure_time) return false;
        const departure = new Date(run.departure_time);
        return departure >= new Date(start) && departure <= new Date(end);
      });
    }

    // Apply source filter (own drivers vs 3rd-party couriers)
    if (source !== 'all') {
      filtered = filtered.filter(run => (source === 'third_party') === isCourierRun(run));
    }

    // Apply search filter. Courier runs match generic courier/vendor terms in
    // v1 — courier NAME search lands with the parts_delivery relationship.
    if (search.trim()) {
      const term = search.trim().toLowerCase();
      filtered = filtered.filter(run => {
        const driverName = run.driver?.user?.display_name?.toLowerCase() || '';
        const courierTerms = isCourierRun(run) ? 'courier hopdrive managed 3rd party uber doordash' : '';
        return driverName.includes(term) || courierTerms.includes(term);
      });
    }

    return filtered;
  }, [allRuns, metric, dateRange, search, source]);

  const runs = filteredRuns.slice(offset, offset + limit);
  const totalCount = filteredRuns.length;

  // Server counts over the tab's range, whatever tile is selected.
  const countWindow = React.useMemo(() => toCountWindow(dateRange), [dateRange]);
  const { counts, refetch: refetchCounts } = usePartsDeliveryCounts({ customerIds: [customerId], window: countWindow });
  const metricCounts = React.useMemo<Record<MetricKey, number>>(() => {
    const buckets = counts?.buckets;
    return {
      all: buckets?.total ?? 0,
      active: buckets?.active ?? 0,
      scheduled: buckets?.upcoming ?? 0,
      completed: buckets?.completed ?? 0,
      cancelled: buckets?.cancelled ?? 0,
    };
  }, [counts]);

  const refetchAll = React.useCallback(() => {
    void refetch();
    refetchCounts();
  }, [refetch, refetchCounts]);
  const tabActions = React.useMemo(() => ({ sync: refetchAll }), [refetchAll]);
  useShellTabActions(tabActions);

  const sourceCounts: Record<SourceKey, number> = React.useMemo(
    () => ({
      all: allRuns.length,
      own: allRuns.filter(r => !isCourierRun(r)).length,
      third_party: allRuns.filter(isCourierRun).length,
    }),
    [allRuns]
  );

  // Reset pagination when filters change
  React.useEffect(() => { setOffset(0); }, [metric, dateRange, search, source]);

  const [loadingVisible, setLoadingVisible] = React.useState(false);
  React.useEffect(() => {
    if (loading) setLoadingVisible(true);
    else setTimeout(() => setLoadingVisible(false), 250);
  }, [loading]);

  return (
    <>
      {/* ── Metric Cards ── */}
      <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {METRICS.map(m => {
          const isSelected = metric === m.key;
          const count = metricCounts[m.key];
          const tileColor = m.bucket ? theme.palette.orderStatus[m.bucket] : theme.palette.text.secondary;
          const tileTint = alpha(tileColor, 0.05);
          return (
            <Card
              key={m.key}
              elevation={0}
              onClick={() => setMetric(m.key)}
              sx={{
                cursor: 'pointer',
                px: 2,
                py: 1.25,
                minWidth: 100,
                flex: '1 1 0',
                border: '1px solid',
                borderColor: isSelected ? tileColor : 'divider',
                bgcolor: isSelected ? tileTint : 'background.paper',
                transition: 'all 0.15s',
                '&:hover': { borderColor: tileColor, bgcolor: tileTint },
              }}
            >
              <Typography variant='h6' sx={{ fontWeight: 700, color: isSelected ? tileColor : 'text.primary', lineHeight: 1.2 }}>
                {count}
              </Typography>
              <Typography variant='caption' sx={{ fontWeight: 600, color: isSelected ? tileColor : 'text.secondary' }}>
                {m.label}
              </Typography>
            </Card>
          );
        })}
      </Box>

      {/* ── Filter Controls ── */}
      <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <TextField
          select
          size='small'
          label='Date Range'
          value={dateRange}
          onChange={e => setDateRange(e.target.value as DateRange)}
          sx={{ minWidth: 140 }}
        >
          {DATE_RANGES.map(r => (
            <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          size='small'
          placeholder={thirdPartyEnabled ? 'Search by name, driver or courier...' : 'Search by name or driver...'}
          value={search}
          onChange={e => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: <Box component={MdSearch} size={18} sx={{ mr: 1, color: 'text.disabled' }} />,
            },
          }}
          sx={{ minWidth: 240 }}
        />
        {thirdPartyEnabled ? (
          <Box sx={{ display: 'flex', gap: 0.75 }} data-testid='pd-source-filter'>
            {SOURCES.map(s => (
              <Chip
                key={s.key}
                label={`${s.label} ${sourceCounts[s.key]}`}
                onClick={() => setSource(s.key)}
                color={source === s.key ? 'primary' : 'default'}
                variant={source === s.key ? 'filled' : 'outlined'}
                sx={{ fontWeight: 600, cursor: 'pointer' }}
              />
            ))}
          </Box>
        ) : null}
      </Box>

      {loadingVisible ? (
        <LinearProgress className={loading ? cls.fadeIn : cls.fadeOut} />
      ) : (
        <div className={cls.loadingPad} />
      )}

      {error ? (
        <>
          <Spacer size={2} />
          <DefaultFallback fullscreen severity='error' message='Failed to fetch parts delivery runs.' />
        </>
      ) : null}

      {!loading && !error && runs.length === 0 ? (
        <>
          <Spacer size={2} />
          <DefaultFallback
            fullscreen
            severity='warning'
            message={metric !== 'all' || dateRange !== 'all' || search
              ? 'No runs match your filters.'
              : 'No parts delivery runs found. Click "New Run" to build one.'
            }
          />
        </>
      ) : null}

      {!error && runs.length > 0 ? (
        <>
          <TablePagination
            className={cls.pagination}
            component='div'
            count={totalCount}
            page={Math.floor(offset / limit)}
            onPageChange={(_e, p) => setOffset(p * limit)}
            rowsPerPage={limit}
            onRowsPerPageChange={e => { setLimit(parseInt(e.target.value, 10)); setOffset(0); }}
            rowsPerPageOptions={[25, 50, 100]}
          />

          <div className={loading ? cls.contentLoading : cls.content}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
              {runs.map(run => {
                // Courier-fulfilled runs: live status derives from the third-party
                // delivery, not run.status (no map card — tracking is vendor-side).
                if (isCourierRun(run)) {
                  return <CourierRunCard key={run.id} run={run} />;
                }

                // A run in motion gets the rich card with stop list + live driver map.
                if (isMobilityRunLive(run.status)) {
                  return <ActiveRunCard key={run.id} run={run} />;
                }

                const driverName = run.driver?.user?.display_name;
                const vehicleLabel = run.vehicle
                  ? [run.vehicle.year, run.vehicle.make, run.vehicle.model].filter(Boolean).join(' ')
                  : null;
                const departure = formatDeparture(run.departure_time);

                return (
                  <Card key={run.id} elevation={1}>
                    <CardActionArea
                      component={Link}
                      to={`/parts-delivery/runs/${run.id}`}
                      sx={{ px: 2.5, py: 2 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* Driver + vehicle (leftmost) */}
                        <Box sx={{ flex: '2 1 220px', minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                          <Avatar sx={{ width: 36, height: 36, bgcolor: 'secondaryAccent.main', fontSize: 13, fontWeight: 700 }}>
                            {driverName ? getInitials(driverName) : <MdPerson size={18} />}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant='body2' sx={{ fontWeight: 700 }} noWrap>
                              {driverName || <Box component='em' sx={{ fontWeight: 400, color: 'text.disabled' }}>Auto-assign</Box>}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                              <MdDirectionsCar size={13} />
                              <Typography variant='caption' noWrap>
                                {vehicleLabel || 'Vehicle unassigned'}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* Date over status pill (right-aligned) */}
                        <Box sx={{ flexShrink: 0, ml: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                          <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            {departure || 'No departure set'}
                          </Typography>
                          <RunStatusChip status={run.status} variant='outlined' sx={{ fontSize: 11, fontWeight: 600, height: 22 }} />
                        </Box>
                      </Box>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Box>

            <TablePagination
              className={cls.pagination}
              component='div'
              count={totalCount}
              page={Math.floor(offset / limit)}
              onPageChange={(_e, p) => setOffset(p * limit)}
              rowsPerPage={limit}
              onRowsPerPageChange={e => { setLimit(parseInt(e.target.value, 10)); setOffset(0); }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </div>
        </>
      ) : null}
    </>
  );
};

const useStyles = (theme: Theme) => {
  const fadeIn = keyframes`0% { opacity: 0; } 100% { opacity: 1; }`;
  const fadeOut = keyframes`0% { opacity: 1; } 100% { opacity: 0; }`;
  const contentFadeIn = keyframes`0% { opacity: 0; } 33% { opacity: 0; } 100% { opacity: 1; }`;

  return {
    header: css`
      background-color: ${theme.palette.background.paper};
      border-bottom: 1px solid ${theme.palette.divider};
    `,
    fadeIn: css`opacity: 1; animation: ${fadeIn} 0.25s ease-in-out;`,
    fadeOut: css`opacity: 0; animation: ${fadeOut} 0.25s ease-in-out;`,
    loadingPad: css`width: 100%; height: 4px;`,
    content: css`opacity: 1; transition: opacity 0.25s ease-in-out; animation: ${contentFadeIn} 0.5s ease-in-out;`,
    contentLoading: css`opacity: 0; transition: opacity 0.25s ease-in-out; user-select: none; pointer-events: none;`,
    pagination: css`border-bottom: 1px solid ${theme.palette.divider}; margin-right: 16px;`,
  };
};

export default ActiveRunsTab;
